import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import axios from "axios"; import { handleAuthError } from "../../utils";
import withAuth from "../withAuth";
import {
  Col,
  Container,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  Card,
  CardBody,
  Badge,
  Spinner,
  Table,
  Button,
  Input,
  FormGroup,
  Label,
  Form
} from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { baseUrl } from '../../Network';

const AdminManagement = () => {
  document.title = "Admin Management | RYD Admin";

  const [loading, setLoading] = useState(true);
  const [admins, setAdmins] = useState([]);
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [newAdmin, setNewAdmin] = useState({
    fullName: '',
    email: '',
    password: '',
    displayName: '',
    role: 1 // Default to admin role
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${baseUrl}/admin/all`);
      setAdmins(response.data.data);
      setLoading(false);
    } catch (error) { handleAuthError(error)
      console.error("Error:", error);
      toast.error("Failed to fetch admins");
      setLoading(false);
    }
  };

  const toggleCreateModal = () => {
    setCreateModal(!createModal);
    setValidationErrors({});
    setNewAdmin({
      fullName: '',
      email: '',
      password: '',
      displayName: '',
      role: 1
    });
    setConfirmPassword('');
  };

  const toggleEditModal = (admin = null) => {
    if (admin) {
      setCurrentAdmin(admin);
      setNewAdmin({
        fullName: admin.fullName,
        email: admin.email,
        password: '',
        displayName: admin.displayName,
        role: admin.role
      });
    }
    setEditModal(!editModal);
    setValidationErrors({});
    setConfirmPassword('');
  };

  const toggleDeleteModal = (admin = null) => {
    setCurrentAdmin(admin);
    setDeleteModal(!deleteModal);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewAdmin(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = (isEdit = false) => {
    const errors = {};

    if (!newAdmin.fullName.trim()) errors.fullName = 'Full name is required';
    if (!newAdmin.email.trim()) errors.email = 'Email is required';
    else if (!/^\S+@\S+\.\S+$/.test(newAdmin.email)) errors.email = 'Email is invalid';
    
    // Only require password for create, not for edit
    if (!isEdit) {
      if (!newAdmin.password) errors.password = 'Password is required';
      else if (newAdmin.password.length < 6) errors.password = 'Password must be at least 6 characters';
      if (newAdmin.password !== confirmPassword) errors.confirmPassword = 'Passwords do not match';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;
    
    newAdmin.role = parseInt(newAdmin.role);

    try {
      setIsSubmitting(true);
      const response = await axios.post(`${baseUrl}/admin/auth/create`, newAdmin);
      toast.success('Admin created successfully');
      fetchAdmins();
      toggleCreateModal();
    } catch (error) { handleAuthError(error)
      console.error("Error:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to create admin");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateAdmin = async (e) => {
    e.preventDefault();

    if (!validateForm(true)) return;
    
    const updateData = {
      fullName: newAdmin.fullName,
      email: newAdmin.email,
      displayName: newAdmin.displayName,
      role: parseInt(newAdmin.role)
    };

    // Only include password if it was provided
    if (newAdmin.password) {
      updateData.password = newAdmin.password;
    }

    try {
      setIsSubmitting(true);
      const response = await axios.put(
        `${baseUrl}/admin/${currentAdmin.id}`,
        updateData
      );
      toast.success('Admin updated successfully');
      fetchAdmins();
      toggleEditModal();
    } catch (error) { handleAuthError(error)
      console.error("Error:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to update admin");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteAdmin = async () => {
    try {
      setIsSubmitting(true);
      const response = await axios.delete(
        `${baseUrl}/admin/${currentAdmin.id}`
      );
      toast.success('Admin deleted successfully');
      fetchAdmins();
      toggleDeleteModal();
    } catch (error) { handleAuthError(error)
      console.error("Error:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to delete admin");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 0:
        return <Badge color="secondary">Viewer</Badge>;
      case 1:
        return <Badge color="primary">Admin</Badge>;
      case 2:
        return <Badge color="info">Sub-Admin</Badge>;
      default:
        return <Badge color="light">Unknown</Badge>;
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Dashboard" breadcrumbItem="Admin Management" />
          <Row>
            <Col lg="12">
              <Row className="align-items-center">
                <Col md={6}>
                  <div className="mb-3">
                    <h5 className="card-title">
                      Admin List{" "}
                      <span className="text-muted fw-normal ms-2">
                        ({admins.length})
                      </span>
                    </h5>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="d-flex flex-wrap align-items-center justify-content-end gap-2 mb-3">
                    <div>
                      <button
                        className="btn btn-primary"
                        onClick={fetchAdmins}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Loading...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-sync-alt me-2"></i>
                            Refresh
                          </>
                        )}
                      </button>
                      <button
                        className="btn btn-success ms-2"
                        onClick={toggleCreateModal}
                      >
                        <i className="fas fa-plus me-2"></i>
                        Create Admin
                      </button>
                    </div>
                  </div>
                </Col>
              </Row>

              {loading ? (
                <div className="text-center mt-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <div className="table-responsive mt-4 shadow rounded" style={{ backgroundColor: '#f9f9fb' }}>
                  <Table className="table align-middle table-hover">
                    <thead className="bg-primary text-white" style={{ borderRadius: '12px' }}>
                      <tr>
                        <th>#</th>
                        <th>Full Name</th>
                        <th>Display Name</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Created At</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {admins.map((admin, index) => (
                        <tr
                          key={index}
                          className={`align-middle ${index % 2 === 0 ? 'bg-white' : 'bg-light'}`}
                          style={{ borderBottom: '1px solid #e0e0e0' }}
                        >
                          <td>{index + 1}</td>
                          <td>{admin.fullName}</td>
                          <td>{admin.displayName}</td>
                          <td>{admin.email}</td>
                          <td>{getRoleBadge(admin.role)}</td>
                          <td>{new Date(admin.createdAt).toLocaleString()}</td>
                          <td>
                            <div className="d-flex gap-2">
                              <Button
                                color="primary"
                                size="sm"
                                onClick={() => toggleEditModal(admin)}
                              >
                                <i className="fas fa-edit"></i>
                              </Button>
                              <Button
                                color="danger"
                                size="sm"
                                onClick={() => toggleDeleteModal(admin)}
                              >
                                <i className="fas fa-trash"></i>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Col>
          </Row>
        </Container>
      </div>

      {/* Create Admin Modal */}
      <Modal isOpen={createModal} toggle={toggleCreateModal}>
        <ModalHeader toggle={toggleCreateModal}>Create New Admin</ModalHeader>
        <ModalBody>
          <Form onSubmit={handleCreateAdmin}>
            <FormGroup>
              <Label>Full Name</Label>
              <Input
                type="text"
                name="fullName"
                value={newAdmin.fullName}
                onChange={handleInputChange}
                invalid={!!validationErrors.fullName}
              />
              {validationErrors.fullName && (
                <div className="text-danger">{validationErrors.fullName}</div>
              )}
            </FormGroup>

            <FormGroup>
              <Label>Display Name</Label>
              <Input
                type="text"
                name="displayName"
                value={newAdmin.displayName}
                onChange={handleInputChange}
              />
            </FormGroup>

            <FormGroup>
              <Label>Email</Label>
              <Input
                type="email"
                name="email"
                value={newAdmin.email}
                onChange={handleInputChange}
                invalid={!!validationErrors.email}
              />
              {validationErrors.email && (
                <div className="text-danger">{validationErrors.email}</div>
              )}
            </FormGroup>

            <FormGroup>
              <Label>Password</Label>
              <Input
                type="password"
                name="password"
                value={newAdmin.password}
                onChange={handleInputChange}
                invalid={!!validationErrors.password}
              />
              {validationErrors.password && (
                <div className="text-danger">{validationErrors.password}</div>
              )}
            </FormGroup>

            <FormGroup>
              <Label>Confirm Password</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                invalid={!!validationErrors.confirmPassword}
              />
              {validationErrors.confirmPassword && (
                <div className="text-danger">{validationErrors.confirmPassword}</div>
              )}
            </FormGroup>

            <FormGroup>
              <Label>Role</Label>
              <Input
                type="select"
                name="role"
                value={newAdmin.role}
                onChange={handleInputChange}
              >
                <option value={0}>Viewer</option>
                <option value={1}>Admin</option>
                <option value={2}>Sub-Admin</option>
              </Input>
            </FormGroup>

            <div className="mt-3 d-flex justify-content-end">
              <Button color="secondary" onClick={toggleCreateModal} className="me-2">
                Cancel
              </Button>
              <Button color="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Creating...
                  </>
                ) : (
                  'Create Admin'
                )}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>

      {/* Edit Admin Modal */}
      <Modal isOpen={editModal} toggle={toggleEditModal}>
        <ModalHeader toggle={toggleEditModal}>Edit Admin</ModalHeader>
        <ModalBody>
          <Form onSubmit={handleUpdateAdmin}>
            <FormGroup>
              <Label>Full Name</Label>
              <Input
                type="text"
                name="fullName"
                value={newAdmin.fullName}
                onChange={handleInputChange}
                invalid={!!validationErrors.fullName}
              />
              {validationErrors.fullName && (
                <div className="text-danger">{validationErrors.fullName}</div>
              )}
            </FormGroup>

            <FormGroup>
              <Label>Display Name</Label>
              <Input
                type="text"
                name="displayName"
                value={newAdmin.displayName}
                onChange={handleInputChange}
              />
            </FormGroup>

            <FormGroup>
              <Label>Email</Label>
              <Input
                type="email"
                name="email"
                value={newAdmin.email}
                onChange={handleInputChange}
                invalid={!!validationErrors.email}
              />
              {validationErrors.email && (
                <div className="text-danger">{validationErrors.email}</div>
              )}
            </FormGroup>

            <FormGroup>
              <Label>Password (leave blank to keep current)</Label>
              <Input
                type="password"
                name="password"
                value={newAdmin.password}
                onChange={handleInputChange}
                invalid={!!validationErrors.password}
              />
              {validationErrors.password && (
                <div className="text-danger">{validationErrors.password}</div>
              )}
            </FormGroup>

            {newAdmin.password && (
              <FormGroup>
                <Label>Confirm Password</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  invalid={!!validationErrors.confirmPassword}
                />
                {validationErrors.confirmPassword && (
                  <div className="text-danger">{validationErrors.confirmPassword}</div>
                )}
              </FormGroup>
            )}

            <FormGroup>
              <Label>Role</Label>
              <Input
                type="select"
                name="role"
                value={newAdmin.role}
                onChange={handleInputChange}
              >
                <option value={0}>Viewer</option>
                <option value={1}>Admin</option>
                <option value={2}>Sub-Admin</option>
              </Input>
            </FormGroup>

            <div className="mt-3 d-flex justify-content-end">
              <Button color="secondary" onClick={toggleEditModal} className="me-2">
                Cancel
              </Button>
              <Button color="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Updating...
                  </>
                ) : (
                  'Update Admin'
                )}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal} toggle={toggleDeleteModal}>
        <ModalHeader toggle={toggleDeleteModal}>Confirm Delete</ModalHeader>
        <ModalBody>
          <p>Are you sure you want to delete admin <strong>{currentAdmin?.fullName}</strong>?</p>
          <p>This action cannot be undone.</p>
          <div className="d-flex justify-content-end mt-3">
            <Button color="secondary" onClick={toggleDeleteModal} className="me-2">
              Cancel
            </Button>
            <Button color="danger" onClick={handleDeleteAdmin} disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Deleting...
                </>
              ) : (
                'Delete Admin'
              )}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <ToastContainer />
    </React.Fragment>
  );
};

export default withAuth(AdminManagement);